/* eslint-disable i18next/no-literal-string */
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import cx from 'classnames';
import type {HeadcountRollup, WireframeReportSpend} from 'client';
import {Link} from 'components/DSYS/Link';
import * as d3 from 'd3';
import {Fragment, ReactNode} from 'react';

import {useFitsParent} from './useFitsParent';
import styles from './WireframeCharts.module.css';
import {
  formatHeadcount,
  formatShare,
  formatUsd,
  shareOf,
  toNumber,
  WIREFRAME_CATEGORIES,
  WireframeCategory,
} from './wireframeReportFormat';

// A segment shows its count only where the count fits; its tooltip always has it.
const SegmentLabel = ({value}: {value: string}) => {
  const {ref, fits} = useFitsParent<HTMLSpanElement>(value);
  return (
    <span ref={ref} className={cx(styles.segmentLabel, !fits && styles.segmentLabelHidden)}>
      {value}
    </span>
  );
};

export const CategorySwatch = ({category}: {category: WireframeCategory}) => (
  <span className={cx(styles.swatch, styles[category.key])} aria-hidden />
);

export const CategoryLegend = () => (
  <ul
    className={atoms({
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      margin: '0',
      padding: '0',
      columnGap: '16',
      rowGap: '8',
      listStyleType: 'none',
    })}
  >
    {WIREFRAME_CATEGORIES.map((category) => (
      <li
        key={category.key}
        className={atoms({display: 'flex', alignItems: 'center', columnGap: '6'})}
      >
        <CategorySwatch category={category} />
        <Text size="small">{category.label}</Text>
      </li>
    ))}
  </ul>
);

const CategoryTooltip = ({
  title,
  category,
  headcount,
  spend,
}: {
  title: string;
  category: WireframeCategory;
  headcount: HeadcountRollup;
  spend: WireframeReportSpend;
}) => {
  const value = toNumber(headcount[category.headcount]);
  return (
    <Stack gap="4">
      <Text tagName="div" size="small" isBold color="inherit">
        {title} · {category.label}
      </Text>
      <Text tagName="div" size="small" color="inherit">
        {formatHeadcount(value)} HC (
        {formatShare(shareOf(value, toNumber(headcount.headcount_fte)))}) ·{' '}
        {formatUsd(spend[category.spend])}
      </Text>
    </Stack>
  );
};

export type CategoryBarRow = {
  key: string | number;
  label: string;
  headcount: HeadcountRollup;
  spend: WireframeReportSpend;
  emphasis?: boolean;
};

/** Each row's headcount split by category, one shared scale so rows compare by length. */
export const CategoryStackedBars = ({rows}: {rows: CategoryBarRow[]}) => {
  const max = Math.max(0, ...rows.map((row) => toNumber(row.headcount.headcount_fte)));
  return (
    <Stack gap="16">
      <CategoryLegend />
      {/* One grid for every row, so the labels take only the longest one's width and stay aligned. */}
      <div className={styles.stackedGrid}>
        {rows.map((row) => {
          const total = toNumber(row.headcount.headcount_fte);
          const segments = WIREFRAME_CATEGORIES.map((category) => ({
            category,
            value: toNumber(row.headcount[category.headcount]),
          })).filter((segment) => segment.value > 0);
          return (
            <Fragment key={row.key}>
              <Text
                size="small"
                isBold={row.emphasis}
                className={styles.barLabel}
                title={row.label}
              >
                {row.label}
              </Text>
              <Box display="flex" alignItems="center" columnGap="8" minWidth="0">
                <div
                  className={styles.track}
                  style={{width: max > 0 ? `${(total / max) * 100}%` : 0}}
                  role="img"
                  aria-label={`${row.label}: ${WIREFRAME_CATEGORIES.map(
                    (category) =>
                      `${category.label} ${formatHeadcount(row.headcount[category.headcount])}`
                  ).join(', ')}`}
                >
                  {segments.map(({category, value}) => (
                    <Tooltip
                      key={category.key}
                      placement="top"
                      title={
                        <CategoryTooltip
                          title={row.label}
                          category={category}
                          headcount={row.headcount}
                          spend={row.spend}
                        />
                      }
                    >
                      <div
                        className={cx(styles.segment, styles[category.key])}
                        style={{flexGrow: value, flexBasis: 0}}
                      >
                        <SegmentLabel value={formatHeadcount(value)} />
                      </div>
                    </Tooltip>
                  ))}
                </div>
                <Text size="small" color="subtle" className={styles.value}>
                  {formatHeadcount(total)}
                </Text>
              </Box>
            </Fragment>
          );
        })}
      </div>
    </Stack>
  );
};

export type RankedBar = {
  key: string | number;
  label: string;
  value: number;
  // Where the label links, in a new tab.
  href: string;
  detail?: ReactNode;
};

/** Single-series horizontal bars, largest first as given, each with its value at the end. */
export const RankedBars = ({items, ariaLabel}: {items: RankedBar[]; ariaLabel: string}) => {
  const max = Math.max(0, ...items.map((item) => item.value));
  return (
    <Stack gap="12" role="list" aria-label={ariaLabel}>
      {items.map((item) => (
        <div key={item.key} className={styles.barRow} role="listitem">
          {/* Long names truncate; the tooltip shows the whole name. */}
          <Tooltip placement="top" title={item.label}>
            <Link
              to={item.href}
              openInNewTab
              hasNoUnderline
              showUnderlineOnHover
              monochromatic
              className={styles.barLabel}
            >
              <Text size="small" color="inherit">
                {item.label}
              </Text>
            </Link>
          </Tooltip>
          <Tooltip
            placement="top"
            title={
              <Stack gap="4">
                <Text tagName="div" size="small" isBold color="inherit">
                  {item.label}
                </Text>
                {item.detail}
              </Stack>
            }
          >
            <Box display="flex" alignItems="center" columnGap="8" minWidth="0">
              <div
                className={cx(styles.bar, styles.single)}
                style={{width: max > 0 ? `${(item.value / max) * 100}%` : 0}}
              />
              <Text size="small" color="subtle" className={styles.value}>
                {formatHeadcount(item.value)}
              </Text>
            </Box>
          </Tooltip>
        </div>
      ))}
    </Stack>
  );
};

const DONUT_SIZE = 200;
const DONUT_THICKNESS = 38;

/** The scope's category mix as a ring, its total in the middle. */
export const CategoryDonut = ({
  headcount,
  spend,
  title,
}: {
  headcount: HeadcountRollup;
  spend: WireframeReportSpend;
  title: string;
}) => {
  const radius = DONUT_SIZE / 2;
  const values = WIREFRAME_CATEGORIES.map((category) => ({
    category,
    value: toNumber(headcount[category.headcount]),
  }));
  const arcs = d3
    .pie<(typeof values)[number]>()
    .value((d) => d.value)
    .sort(null)(values.filter((d) => d.value > 0));
  const arc = d3
    .arc<d3.PieArcDatum<(typeof values)[number]>>()
    .innerRadius(radius - DONUT_THICKNESS)
    .outerRadius(radius);
  const ring = d3
    .arc()
    .innerRadius(radius - DONUT_THICKNESS)
    .outerRadius(radius)
    .startAngle(0)
    .endAngle(2 * Math.PI);
  return (
    <Box position="relative" flexShrink={0} className={styles.donut}>
      <svg
        width={DONUT_SIZE}
        height={DONUT_SIZE}
        viewBox={`${-radius} ${-radius} ${DONUT_SIZE} ${DONUT_SIZE}`}
        role="img"
        aria-label={`${title} headcount by category: ${values
          .map(({category, value}) => `${category.label} ${formatHeadcount(value)}`)
          .join(', ')}`}
      >
        {arcs.length === 0 ? (
          <path d={ring({} as d3.DefaultArcObject) ?? undefined} className={styles.donutEmpty} />
        ) : (
          arcs.map((slice) => (
            <path
              key={slice.data.category.key}
              d={arc(slice) ?? undefined}
              className={cx(styles.donutArc, styles[slice.data.category.key])}
            />
          ))
        )}
      </svg>
      <Box
        position="absolute"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingX="24"
        textAlign="center"
        className={styles.donutCenter}
      >
        <Text size="small" isBold>
          {formatHeadcount(headcount.headcount_fte)} HC
        </Text>
        <Text size="xsmall" color="subtle">
          {formatUsd(spend.total_usd)}
        </Text>
      </Box>
    </Box>
  );
};
